import * as React from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  useWorkspaceLoaderData,
  type VisualizerInitData,
} from "@/shared/components/visualization/loader";
import { VizConfigBuilder } from "@/shared/config/VizConfig";
import {
  Datasource,
  State,
  VizStateContext,
  VizStateDispatchContext,
  actions,
  reducer,
} from "@/shared/state/visualization";

export function VisualizationStateProvider({
  children,
}: React.PropsWithChildren) {
  const { fileId } = useParams();
  const location = useLocation();
  const workspaceResult = useWorkspaceLoaderData();

  const fileIds = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.getAll("file");
  }, [location.search]);

  const datasetId = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("dataset") ?? undefined;
  }, [location.search]);

  const [state, dispatch] = React.useReducer(
    reducer,
    initializeVizState(fileId, fileIds, datasetId, workspaceResult),
  );

  // When the file param from /files/:fileId changes, update the state.
  React.useEffect(
    function onFileChanged() {
      if (!fileId) {
        return;
      }

      dispatch(actions.putFiles([{ fileId }]));
    },
    [fileId],
  );

  return (
    <VizStateContext.Provider value={state}>
      <VizStateDispatchContext.Provider value={dispatch}>
        {children}
      </VizStateDispatchContext.Provider>
    </VizStateContext.Provider>
  );
}

function initializeVizState(
  fileId: string | undefined,
  fileIds: string[],
  datasetId: string | undefined,
  workspaceInitResult: VisualizerInitData,
): State {
  // If we have a workspace result from the loader, use that directly
  if (workspaceInitResult?.type === "workspace") {
    return workspaceInitResult.value;
  }

  const vizConfigBuilder = new VizConfigBuilder();

  // Build datasources from URL params
  const datasources: Datasource[] = [];

  if (datasetId) {
    datasources.push({ type: "dataset", value: { datasetId } });
  }

  if (fileId) {
    // From /files/:fileId route
    datasources.push({ type: "file", value: { fileId } });
  }

  // From ?file= search params
  fileIds.forEach((id) => {
    datasources.push({ type: "file", value: { fileId: id } });
  });

  vizConfigBuilder.setDatasources(datasources);

  return vizConfigBuilder.build().toObject();
}
